import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appConfig } from '../config/appConfig';
import { useApiService } from '../services/useApiService';
import LoadingSpinner from '../components/LoadingSpinner';
import NaibLogo from '../components/NaibLogo';
import { EnvelopeIcon, KeyIcon, BuildingOfficeIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

/** Currency line items: always two decimal places (avoids float noise from API or addition). */
function formatUsdAmount(amount) {
  const n = Number(amount);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
}

function Onboarding() {
  const navigate = useNavigate();
  const { productId: productIdParam } = useParams();
  const { onboarding } = useApiService();
  const resolvedProductId =
    productIdParam != null && productIdParam !== ''
      ? parseInt(productIdParam, 10)
      : appConfig.productId;
  const productIdForApi = Number.isFinite(resolvedProductId) ? resolvedProductId : appConfig.productId;
  const [formData, setFormData] = useState({
    entityName: '',
    contactName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userCount: 1
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [productInfo, setProductInfo] = useState(null);
  const [totalCost, setTotalCost] = useState(0);
  const [showUserInfoTooltip, setShowUserInfoTooltip] = useState(false);
  const [step, setStep] = useState(1);

  const goToStep = (nextStep) => {
    setError('');
    setStep(nextStep);
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.contactName.trim()) {
        setError('Contact name is required');
        return;
      }
      goToStep(2);
      return;
    }
    if (step === 2) {
      if (!formData.email.trim()) {
        setError('Email is required');
        return;
      }
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        setError(passwordError);
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      goToStep(3);
    }
  };

  // Password validation
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  useEffect(() => {
    const fetchProductInfo = async () => {
      try {
        setLoading(true);
        const response = await onboarding.getProductInfo(productIdForApi);
        setProductInfo(response);
        // Initialize total cost will be calculated by the other useEffect
      } catch (error) {
        console.error('Error fetching product info:', error);
        setError('Failed to load product information. ' + (error.message || 'Please try again.'));
      } finally {
        setLoading(false);
      }
    };

    fetchProductInfo();
  }, [onboarding, productIdForApi]);

  // Calculate total cost (fixed at 1 user for signup, billing happens later)
  useEffect(() => {
    if (!productInfo) return;

    const basePrice = productInfo.basePrice || 0;
    const userRate = productInfo.userRate || 0;
    const userCount = 1; // Fixed at 1 user for signup
    const userCost = userCount * userRate;
    const total = basePrice + userCost;
    setTotalCost(Math.round(total * 100) / 100);
  }, [productInfo]);

  // Close tooltip when clicking outside
  const tooltipRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowUserInfoTooltip(false);
      }
    };

    if (showUserInfoTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserInfoTooltip]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError('Please accept the terms and conditions to continue');
      return;
    }

    // Validate required fields
    if (!formData.contactName.trim()) {
      setError('Contact name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Validate password
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Use contact name as entity name if entity name is not provided
      const finalEntityName = formData.entityName.trim() || formData.contactName.trim();
      const finalName = formData.contactName.trim();

      const startResult = await onboarding.start({
        email: formData.email.trim(),
        password: formData.password,
        entityName: finalEntityName,
        contactName: finalName,
        name: finalName, // This is for the Customer.name field
        selectedFeatures: [], // Features are now included by default, no selection needed
        productId: productIdForApi
      });

      navigate('/verify-email-sent', {
        state: {
          email: formData.email,
          emailSent: startResult?.emailSent !== false
        }
      });
    } catch (error) {
      const apiMessage = error.response?.data?.error;
      setError(
        apiMessage && !/status code|failed to complete/i.test(apiMessage)
          ? apiMessage
          : 'We could not complete registration. Try again, or contact your organization administrator.'
      );
    } finally {
      setLoading(false);
    }
  };

  const TermsModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Onahiri Acceptable Use Policy</h3>
          <div className="text-sm text-gray-700 space-y-4">
            <p><strong>Effective Date:</strong> June 23, 2025</p>
            <p>Please read this policy carefully before accessing or using the Onahiri Platform.</p>
            
            <p>The Onahiri Platform (the "Platform" or "Onahiri Apps") includes software applications made available by Onahiri Technologies Pvt. Ltd. ("Onahiri", "we", "our", or "us") through web interfaces and mobile applications. Access to the Platform is provided through an agreement between Onahiri and your employer or affiliated organization (the "Organization"). This Acceptable Use Policy ("Policy") sets out the rules and responsibilities that apply to all users of the Onahiri Platform.</p>
            
            <p>This Policy forms part of our Terms of Use and works in conjunction with our Privacy Policy. Your Organization may also enforce additional internal policies.</p>

            <h4 className="font-semibold text-gray-900">1. Who We Are and How to Contact Us</h4>
            <p>Effimetic Solutions LLP. is the provider of the Onahiri Platform.</p>
            <p>If you have any questions about this Policy:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Email: make.easy@effimetic.com</li>
            </ul>

            <h4 className="font-semibold text-gray-900">2. Acceptance and Scope</h4>
            <p>By accessing or using the Platform, you confirm that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You have read and understood this Policy.</li>
              <li>You agree to comply with all of its terms.</li>
              <li>You are authorized by your Organization to use the Platform.</li>
            </ul>
            <p>If you do not agree with any part of this Policy, you must not use the Platform.</p>

            <h4 className="font-semibold text-gray-900">3. Policy Changes</h4>
            <p>We may update this Policy from time to time. You are responsible for reviewing the most recent version, which is always available under Settings &gt; Help. Continued use of the Platform constitutes acceptance of any changes.</p>

            <h4 className="font-semibold text-gray-900">4. Your Responsibilities</h4>
            <p>You are responsible for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Using the Platform in a lawful, ethical, and respectful manner.</li>
              <li>Ensuring your content is appropriate for a professional environment.</li>
              <li>Maintaining the confidentiality of your login credentials.</li>
              <li>Complying with your Organization's internal IT, data protection, and communication policies.</li>
            </ul>
            <p>You may not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Share your account or access the Platform using another person's credentials.</li>
              <li>Misrepresent your identity or affiliation.</li>
              <li>Use the Platform for unlawful, harmful, or deceptive activities.</li>
              <li>Upload, send, or store content that violates our standards (see Section 6).</li>
              <li>Use the Platform for personal business, commercial gain, or external promotion.</li>
            </ul>

            <h4 className="font-semibold text-gray-900">5. Privacy</h4>
            <p>Your Organization may monitor and access activity and content on the Platform. Do not use the Platform to store or share any personal or private information you wish to remain confidential.</p>

            <h4 className="font-semibold text-gray-900">6. Content Standards</h4>
            <p>These standards apply to all content you add, post, upload, send, or share on the Platform ("Your Content").</p>
            <p>Your Content must:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Be accurate and not misleading.</li>
              <li>Be respectful and non-discriminatory.</li>
              <li>Comply with applicable laws, regulations, and internal policies.</li>
              <li>Only contain opinions you genuinely hold.</li>
            </ul>
            <p>Your Content must not:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Defame, threaten, harass, or humiliate others.</li>
              <li>Include hate speech or promote violence or discrimination.</li>
              <li>Infringe on intellectual property, contractual obligations, or legal rights.</li>
              <li>Contain malware, viruses, or disruptive code.</li>
              <li>Promote illegal or unethical behavior.</li>
              <li>Impersonate others or falsely represent affiliation.</li>
              <li>Include unsolicited advertising or external links.</li>
            </ul>

            <h4 className="font-semibold text-gray-900">7. Rights</h4>
            <p>By using the Platform, you grant:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Onahiri a non-exclusive, royalty-free, worldwide license to store, display, analyze, and use Your Content for operating and improving the Platform.</li>
              <li>Your Organization the right to use and retain Your Content for business, compliance, or operational needs.</li>
              <li>Authorized users in your Organization the right to access and interact with Your Content when shared in group areas or communication channels.</li>
            </ul>
            <p>Your Content may be retained even after deletion if legally required or if mandated under a contract with your Organization.</p>

            <h4 className="font-semibold text-gray-900">8. Use of Platform Content</h4>
            <p>Unless otherwise indicated, all intellectual property in the Onahiri Platform (excluding user content) belongs to Onahiri. You shall not reuse, reproduce, distribute, or commercially exploit any part of the Platform or its content without written permission.</p>

            <h4 className="font-semibold text-gray-900">9. Third-Party Services and Links</h4>
            <p>The Platform may contain links to external websites or services, including those provided by your Organization. Onahiri does not control or endorse these third-party resources and is not responsible for their content or reliability.</p>

            <h4 className="font-semibold text-gray-900">10. Content from Other Users</h4>
            <p>The Platform may include user-generated content. The views expressed by other users do not reflect those of Onahiri. We do not routinely monitor or verify such content.</p>

            <h4 className="font-semibold text-gray-900">11. Enforcement and Violations</h4>
            <p>Violating this Policy may result in:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Suspension or permanent revocation of your access.</li>
              <li>Removal or restriction of Your Content.</li>
              <li>Notification to your Organization.</li>
              <li>Formal warnings or legal action.</li>
              <li>Disclosure to law enforcement if required.</li>
            </ul>
            <p>Onahiri reserves the right to take any measures necessary to address violations.</p>

            <h4 className="font-semibold text-gray-900">12. Limitation of Liability</h4>
            <p>The Onahiri Platform is provided for professional and organizational use. We disclaim all implied warranties and are not responsible for:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Loss of content or data.</li>
              <li>Disruptions in service.</li>
              <li>Actions taken by your Organization or other users.</li>
              <li>Any indirect, incidental, or consequential damages arising from your use of the Platform.</li>
            </ul>
            <p>Our liability is limited to the extent permitted by law.</p>

            <h4 className="font-semibold text-gray-900">13. Reporting Misuse or Inappropriate Content</h4>
            <p>You may report content that violates this Policy through:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your Organization's designated reporting channels</li>
              <li>Emailing Onahiri directly at make.easy@effimetic.com</li>
            </ul>
            <p>Note: Not all content you disagree with will be removed. Onahiri and your Organization will assess whether it violates this Policy.</p>

            <h4 className="font-semibold text-gray-900">14. Support</h4>
            <p>If you have questions or require assistance:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Contact your Organization's administrator or IT support.</li>
              <li>For technical issues or policy concerns, contact us at make.easy@effimetic.com</li>
            </ul>
            <p>We may refer your inquiry to your Organization depending on the nature of the issue.</p>
          </div>
          <div className="items-center px-4 py-3 mt-6">
            <button
              onClick={() => setShowTerms(false)}
              className="px-4 py-2 bg-gray-900 text-white text-base font-medium rounded-lg w-full shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  } 

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full bg-white rounded-2xl shadow-xl flex flex-col md:flex-row">
        {/* Left Column - Registration Form */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 2xl:p-12 3xl:p-14 border-b md:border-b-0 md:border-r">
          <div className="pb-4">
            <NaibLogo logoClassName="h-9 w-9" nameClassName="h-7 w-auto max-w-[180px]" alt="Onahiri" />
          </div>
          
          <div className="text-sm font-light text-gray-500 pb-4">
            Create your account to start your {productInfo?.trialDays ? `${productInfo.trialDays}-day ` : ''}free trial
          </div>
          <div className="mb-6 flex items-center gap-2 text-xs font-medium text-gray-500">
            {['You', 'Account', 'Review'].map((label, index) => {
              const number = index + 1;
              const active = step === number;
              const done = step > number;
              return (
                <React.Fragment key={label}>
                  <span className={active || done ? 'text-gray-900' : ''}>
                    {number}. {label}
                  </span>
                  {number < 3 ? <span className="text-gray-300">/</span> : null}
                </React.Fragment>
              );
            })}
          </div>

          <form className="flex flex-col" onSubmit={handleSubmit} id="onboarding-form">
            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {step === 1 && (
              <>
            <div className="pb-2">
              <label htmlFor="contactName" className="block mb-2 text-xs font-medium text-gray-900">
                Contact Name
              </label>
              <div className="relative text-gray-400">
                <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                  <BuildingOfficeIcon className="h-5 w-5" />
                </span>
                <input
                  id="contactName"
                  name="contactName"
                  type="text"
                  className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 text-xs rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                  placeholder="Your name"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                />
              </div>
            </div>

            <div className="pb-2">
              <label htmlFor="entityName" className="block mb-2 text-xs font-medium text-gray-900">
                Company Name <span className="text-xs text-gray-500">(optional)</span>
              </label>
              <div className="relative text-gray-400">
                <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                  <BuildingOfficeIcon className="h-5 w-5" />
                </span>
                <input
                  id="entityName"
                  name="entityName"
                  type="text"
                  className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 text-xs rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                  placeholder="Your company name (optional)"
                  value={formData.entityName}
                  onChange={(e) => setFormData({ ...formData, entityName: e.target.value })}
                />
              </div>
            </div>
              </>
            )}

            {step === 2 && (
              <>
            <div className="pb-2">
              <label htmlFor="email" className="block mb-2 text-xs font-medium text-gray-900">
                Email
              </label>
              <div className="relative text-gray-400">
                <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                  <EnvelopeIcon className="h-5 w-5" />
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 text-xs rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div className="pb-2">
              <label htmlFor="password" className="block mb-2 text-xs font-medium text-gray-900">
                Password
              </label>
              <div className="relative text-gray-400">
                <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                  <KeyIcon className="h-5 w-5" />
                </span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  className="pl-12 mb-1 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 text-xs rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                  placeholder="••••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character.
              </p>
            </div>

            <div className="pb-6">
              <label htmlFor="confirmPassword" className="block mb-2 text-xs font-medium text-gray-900">
                Confirm Password
              </label>
              <div className="relative text-gray-400">
                <span className="absolute inset-y-0 left-0 flex items-center p-1 pl-3">
                  <KeyIcon className="h-5 w-5" />
                </span>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  className="pl-12 mb-2 bg-gray-50 text-gray-600 border focus:border-transparent border-gray-300 text-xs rounded-lg ring ring-transparent focus:ring-1 focus:outline-none focus:ring-gray-400 block w-full p-2.5 py-3 px-4"
                  placeholder="••••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                />
              </div>
            </div>
              </>
            )}

            {step === 3 && (
            <div className="flex items-center mb-6">
              <input
                id="terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="w-4 h-4 text-gray-900 bg-gray-100 border-gray-300 rounded focus:ring-gray-500"
              />
              <label htmlFor="terms" className="ml-2 text-xs font-medium text-gray-500">
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  className="text-gray-900 hover:underline"
                >
                  Acceptable Use Policy
                </button>
              </label>
            </div>
            )}

            <div className="flex gap-3 mt-2">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => goToStep(step - 1)}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Back
                </button>
              ) : null}
              {step < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
                >
                  Continue
                </button>
              ) : null}
            </div>
          </form>
        </div>

        {/* Right Column - Features and Pricing */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 2xl:p-12 3xl:p-14 bg-gray-50">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">What's Included</h2>
            <p className="text-xs text-gray-500">All features are included with your subscription</p>
          </div>

          {/* Features List (Read-only) */}
          {productInfo && (
            <div className="mb-6">
              <div className="space-y-2">
                {productInfo.features.map(feature => (
                  <div key={feature.id} className="flex items-start p-3 border rounded-lg bg-white">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <div className="h-2 w-2 bg-gray-900 rounded-full mr-2 mt-1"></div>
                        <label className="text-xs font-medium text-gray-900">
                          {feature.name}
                        </label>
                      </div>
                      <p className="ml-4 text-xs text-gray-500 mt-1">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Summary */}
          {productInfo && (
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Pricing Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Base Price</span>
                  <span className="text-xs font-medium text-gray-900">${formatUsdAmount(productInfo.basePrice)}/mo</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1 relative" ref={tooltipRef}>
                    <span className="text-xs text-gray-600">
                      Price Per Active User
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowUserInfoTooltip(!showUserInfoTooltip)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label="Information about active user pricing"
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                    </button>
                    {showUserInfoTooltip && (
                      <div className="absolute left-0 top-6 z-10 w-48 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                        Billable only to active users for a month
                        <div className="absolute -top-1 left-3 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-medium text-gray-900">
                    ${formatUsdAmount(productInfo.userRate)}/mo
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="text-xs font-medium text-gray-900">Total (after trial)</span>
                    <span className="text-sm font-semibold text-gray-900">${formatUsdAmount(totalCost)}/mo</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    First {productInfo.trialDays} days are free. You won't be charged during the trial period.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 3 ? (
          <button
            type="submit"
            form="onboarding-form"
            disabled={loading || !acceptedTerms}
            className="w-full text-white bg-gray-900 hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-3 text-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
          ) : (
            <p className="text-xs text-gray-500">Use Continue on the left to finish account details, then review terms here.</p>
          )}
        </div>
      </div>

      {showTerms && <TermsModal />}
    </div>
  );
}

export default Onboarding; 